import { useState, type JSX, type ReactNode } from 'react';
import { IconButton } from '@mui/material';
import { DownloadIcon } from 'lucide-react';
import { FlashingDotsAnimation } from '@/components/Animations/FlashingDotsAnimation';
import { corePalette } from '@/themes/colours';
import CustomModal from '@/components/Common/CustomModal';
import CustomDataGrid from '@/components/Common/DataGrid';
import type { IDataFile } from '@/types/FileTracker/FileTracker.types';
import { useZeroDashSdk } from '@/contexts/ZeroDashSdkContext';

interface IProps {
  files: IDataFile[];
  entityName: string;
}

export function FilesButton({
  files,
  entityName,
}: IProps): JSX.Element {
  const zeroDashSdk = useZeroDashSdk();
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [filesDownloading, setFilesDownloading] = useState<Record<string, boolean>>({});

  const downloadFile = (file: IDataFile): void => {
    setFilesDownloading((prev) => ({ ...prev, [file.fileId]: true }));
    zeroDashSdk.filetracker.downloadFile(file.fileId)
      .then((blob) => {
        const url = window.URL.createObjectURL(blob);
        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.download = file.fileName;
        document.body.appendChild(anchor);
        anchor.click();
        document.body.removeChild(anchor);
        window.URL.revokeObjectURL(url);
      })
      .finally(() => {
        setFilesDownloading((prev) => ({ ...prev, [file.fileId]: false }));
      });
  };

  const downloadCell = ({ row: fileRow }): ReactNode => (
    fileRow.file
      ? (
        <IconButton
          onClick={() => downloadFile(fileRow.file)}
          disabled={filesDownloading[fileRow.file.fileId]}
        >
          {filesDownloading[fileRow.file.fileId]
            ? <FlashingDotsAnimation colour={corePalette.offBlack100} />
            : <DownloadIcon />}
        </IconButton>
      )
      : undefined
  );

  return (
    <>
      <IconButton onClick={() => setModalOpen(true)}>
        <DownloadIcon />
      </IconButton>
      {modalOpen && (
        <CustomModal
          title={`Download files for ${entityName}`}
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          showActions={{
            cancel: false,
            confirm: false,
            secondary: false,
          }}
          content={(
            <CustomDataGrid
              columns={[
                { field: 'fileName', headerName: 'File Name', flex: 1 },
                { field: 'file', renderCell: downloadCell },
              ]}
              rows={files.map((f) => ({
                id: f.fileId,
                fileName: f.fileName,
                file: f,
              }))}
            />
          )}
        />
      )}
    </>
  );
}